import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Chip,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import groupService from '../services/groupService';
import useCan from '../hooks/useCan';
import { useOptionalPermissions } from '../context/permissionContextCore';
import BarraElenco from '../components/common/BarraElenco';
import ConfirmDialog from '../components/ConfirmDialog';
import { CONTENITORE_TABELLA, TABELLA_PAGINA, TESTATA_TABELLA } from '../components/common/stiliTabella';

const STATO_VUOTO = 'Nessun gruppo. Il primo gruppo si crea da qui.';

const DESCRIZIONE_PAGINA =
    'Il gruppo è il contenitore di ciò su cui si lavora — code di lavoro e tipi di Codex — ' +
    'mentre il ruolo resta ciò che si può fare. Apri un gruppo per configurarlo e per vedere ' +
    'chi ne fa parte.';

/**
 * L'elenco dei gruppi. Il gruppo si apre su una pagina sua, `/groups/:id`.
 *
 * Il gruppo è il contenitore di ciò su cui si lavora — code e tipi di Codex — mentre il
 * ruolo resta ciò che si può fare. In questa fase nessun filtro legge queste righe: si
 * possono creare gruppi e riempirli senza che nessun utente veda qualcosa di diverso.
 *
 * Il dettaglio non è più un pannello sotto questa tabella: senza un URL proprio un gruppo
 * non è linkabile, e «guarda il reparto Fatture» resta un'istruzione a fare clic. Vedi
 * `GroupDetail`, che porta configurazione e membri affiancati.
 */
export default function Groups() {
    const can = useCan();
    // Eliminare un gruppo toglie le concessioni su di esso, forse anche a chi elimina.
    const refreshPermissions = useOptionalPermissions()?.refresh;
    const navigate = useNavigate();
    const [gruppi, setGruppi] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [actionError, setActionError] = useState(null);
    const [ricerca, setRicerca] = useState('');
    const [dialogoAperto, setDialogoAperto] = useState(false);
    const [nuovo, setNuovo] = useState({ code: '', label: '', description: '' });
    // Eliminare un gruppo toglie in un colpo i ruoli di tutti i suoi membri e non si annulla:
    // un clic solo sul cestino non basta.
    const [daEliminare, setDaEliminare] = useState(null);
    const [eliminando, setEliminando] = useState(false);
    // Il testo resta durante la dissolvenza del dialogo, quando daEliminare e' gia' null
    const ultimoDaEliminare = useRef(null);
    if (daEliminare) ultimoDaEliminare.current = daEliminare;

    const puoCreare = can('groups.create');
    const puoEliminare = can('groups.delete');

    const load = useCallback(() => {
        setLoading(true);
        setError(false);
        groupService.lista().then(
            (elenco) => {
                setGruppi(elenco || []);
                setLoading(false);
            },
            () => {
                setError(true);
                setLoading(false);
            },
        );
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const creaGruppo = () => {
        setActionError(null);
        groupService.crea(nuovo).then(
            (creato) => {
                setDialogoAperto(false);
                setNuovo({ code: '', label: '', description: '' });
                // Si va subito sul gruppo appena creato: e' vuoto, e un gruppo vuoto non
                // cambia cio' che vede nessuno finche' non gli si mette dentro qualcosa.
                navigate(`/groups/${creato.id}`);
            },
            (err) => setActionError(
                err?.response?.data?.message || 'Non è stato possibile creare il gruppo.',
            ),
        );
    };

    const eliminaGruppo = (id) => {
        setActionError(null);
        setEliminando(true);
        groupService.elimina(id).then(
            () => {
                setGruppi((prev) => prev.filter((gruppo) => gruppo.id !== id));
                refreshPermissions?.();
            },
            (err) => setActionError(
                err?.response?.data?.message || 'Non è stato possibile eliminare il gruppo.',
            ),
        ).finally(() => {
            setEliminando(false);
            setDaEliminare(null);
        });
    };

    const messaggioEliminazione = (gruppo) => {
        if (!gruppo) return '';
        const membri = gruppo.memberCount || 0;
        const chi = membri === 0
            ? 'Nessuno ha un ruolo in questo gruppo.'
            : `${membri === 1 ? 'Il suo membro perde' : `I suoi ${membri} membri perdono`} i ruoli concessi qui.`;
        return `Eliminare il gruppo «${gruppo.label}»? ${chi} L'operazione non si può annullare.`;
    };

    if (loading) {
        return <Typography variant="body2" color="text.secondary">Caricamento…</Typography>;
    }

    if (error) {
        return (
            <Alert
                severity="error"
                action={<Button color="inherit" size="small" onClick={load}>Riprova</Button>}
            >
                Non è stato possibile caricare i gruppi.
            </Alert>
        );
    }

    const filtrati = gruppi.filter((gruppo) => {
        const q = ricerca.trim().toLowerCase();
        if (!q) return true;
        return `${gruppo.label} ${gruppo.code} ${gruppo.description || ''}`
            .toLowerCase()
            .includes(q);
    });

    return (
        <Box>
            <Typography variant="h4" gutterBottom>Gruppi</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {DESCRIZIONE_PAGINA}
            </Typography>

            <BarraElenco
                ricerca={ricerca}
                onRicerca={setRicerca}
                placeholderRicerca="Cerca..."
                conteggio={
                    ricerca.trim()
                        ? `${filtrati.length} di ${gruppi.length} ${gruppi.length === 1 ? 'gruppo' : 'gruppi'}`
                        : `${gruppi.length} ${gruppi.length === 1 ? 'gruppo' : 'gruppi'}`
                }
                azione={puoCreare && (
                    <Button variant="contained" onClick={() => setDialogoAperto(true)}>
                        Nuovo gruppo
                    </Button>
                )}
            />

            {actionError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
                    {actionError}
                </Alert>
            )}

            {gruppi.length === 0 ? (
                <Alert severity="info">{STATO_VUOTO}</Alert>
            ) : (
                <TableContainer {...CONTENITORE_TABELLA}>
                    <Table size="small" sx={TABELLA_PAGINA}>
                        <TableHead>
                            <TableRow sx={TESTATA_TABELLA}>
                                <TableCell>Gruppo</TableCell>
                                <TableCell>Codice</TableCell>
                                <TableCell>Descrizione</TableCell>
                                <TableCell align="right">Tipi di Codex</TableCell>
                                <TableCell align="right">Code</TableCell>
                                <TableCell align="right">Membri</TableCell>
                                {puoEliminare && <TableCell align="right">Azioni</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtrati.map((gruppo) => (
                                <TableRow
                                    key={gruppo.id}
                                    hover
                                    onClick={() => navigate(`/groups/${gruppo.id}`)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell>{gruppo.label}</TableCell>
                                    <TableCell>
                                        {/* Il codice e' una pillola contornata come in Tipi
                                            di Codex: e' l'identificatore stabile, e la
                                            stessa cosa si legge allo stesso modo ovunque. */}
                                        <Chip
                                            label={gruppo.code}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>{gruppo.description || '—'}</TableCell>
                                    <TableCell align="right">
                                        {(gruppo.codexTypeIds || []).length}
                                    </TableCell>
                                    <TableCell align="right">
                                        {(gruppo.candidateGroups || []).length}
                                    </TableCell>
                                    <TableCell align="right">{gruppo.memberCount}</TableCell>
                                    {puoEliminare && (
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1}
                                                   justifyContent="flex-end">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    title="Elimina gruppo"
                                                    aria-label={`elimina ${gruppo.code}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDaEliminare(gruppo);
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={dialogoAperto} onClose={() => setDialogoAperto(false)} fullWidth>
                <DialogTitle>Nuovo gruppo</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Codice"
                            size="small"
                            value={nuovo.code}
                            helperText="Identificatore stabile: non si cambia dopo la creazione."
                            onChange={(e) => setNuovo((prev) => ({ ...prev, code: e.target.value }))}
                        />
                        <TextField
                            label="Etichetta"
                            size="small"
                            value={nuovo.label}
                            onChange={(e) =>
                                setNuovo((prev) => ({ ...prev, label: e.target.value }))}
                        />
                        <TextField
                            label="Descrizione"
                            size="small"
                            value={nuovo.description}
                            onChange={(e) =>
                                setNuovo((prev) => ({ ...prev, description: e.target.value }))}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogoAperto(false)}>Annulla</Button>
                    <Button
                        variant="contained"
                        onClick={creaGruppo}
                        disabled={!nuovo.code.trim() || !nuovo.label.trim()}
                    >
                        Crea
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={Boolean(daEliminare)}
                title="Elimina gruppo"
                message={messaggioEliminazione(ultimoDaEliminare.current)}
                busy={eliminando}
                onConfirm={() => eliminaGruppo(daEliminare.id)}
                onClose={() => setDaEliminare(null)}
            />
        </Box>
    );
}
